import './styles/Table.css';

import get from 'lodash/get';
import isNil from 'lodash/isNil';
import { Fragment, PureComponent } from 'react';

import { List } from 'immutable';
import PropTypes from 'prop-types';
import Scrollbars from 'react-custom-scrollbars-2';
import { FormattedMessage } from 'react-intl';
import handleEvent from 'utils/handleEvent';

const pagination = [20, 50, 100, 200, 500, 1000];
const initState = (columns) => {
  return {
    selectedDevices: new List(),
    elapsedTime: 0,
    page: 1,
    rowsPerPage: pagination[0],
    filters: columns.reduce((acc, column) => {
      acc[column] = '';
      return acc;
    }, {}),
  };
};

export default class Table extends PureComponent {
  static propTypes = {
    columns: PropTypes.array.isRequired,
    data: PropTypes.instanceOf(List).isRequired,
    disableFooter: PropTypes.bool,
    msg: PropTypes.object.isRequired,
    selectedSnapshot: PropTypes.string.isRequired,
    filterable: PropTypes.bool,
    tableTitle: PropTypes.string.isRequired,
    snapshotsMsg: PropTypes.object.isRequired,
  };

  static defaultProps = {
    disableFooter: false,
    filterable: false,
  };

  constructor(props) {
    super(props);
    this.state = initState(props.columns);
  }

  componentDidUpdate(prevProps) {
    const { selectedSnapshot, columns } = this.props;
    if (selectedSnapshot !== prevProps.selectedSnapshot) {
      this.setState(initState(columns));
    }
  }

  getData(dataAfterAppliedFilters) {
    const { page, rowsPerPage } = this.state;
    const paginate = (array, rows, pageNumber) => {
      const p = pageNumber - 1; // because pages logically start with 1, but technically with 0
      return array.slice(p * rows, (p + 1) * rows);
    };
    return paginate(dataAfterAppliedFilters, rowsPerPage, page);
  }

  getColumnContent = (column, device) => {
    return get(device, [column], '');
  };

  handleOnChangeFilter = handleEvent((e) => {
    const { filters } = this.state;
    const column = e.target.getAttribute('data-id');
    this.setState({
      page: 1,
      filters: {
        ...filters,
        [column]: e.target.value,
      },
    });
  });

  handleOnChangeRowsPerPage = handleEvent((e) =>
    this.setState({ rowsPerPage: Number(e.target.value) }),
  );

  handlePageClick = (page) => handleEvent(() => this.setState({ page }));

  applyFilters() {
    const { data } = this.props;
    const { filters } = this.state;
    const appliedFilters = Object.keys(filters).filter(
      (f) => typeof filters[f] === 'string' && filters[f].trim().length > 0,
    );
    if (appliedFilters.length === 0) {
      return data.toJS();
    }

    return data.toJS().reduce((acc, dev) => {
      const cntValidConditions = appliedFilters.reduce((acc2, column) => {
        if (isNil(dev[column])) {
          return acc2;
        }
        if (
          dev[column]
            .toString()
            .toLowerCase()
            .includes(filters[column].toLowerCase())
        ) {
          return acc2 + 1;
        }
        return acc2;
      }, 0);
      if (cntValidConditions === appliedFilters.length) {
        acc.push(dev);
      }
      return acc;
    }, []);
  }

  renderHeader(tableColumns) {
    const { msg, filterable } = this.props;
    return (
      <thead>
        <tr
          className={`snapshots-tabs-table__tr ${filterable ? 'snapshots-tabs-table__no-bottom-border' : ''}`}
        >
          {tableColumns.map((column) => {
            return (
              <th
                key={column}
                className={`snapshots-tabs-table__td ${filterable ? 'snapshots-tabs-table__no-bottom-border' : ''}`}
              >
                {get(msg, ['columns', column], column)}
              </th>
            );
          })}
        </tr>
        {filterable ? (
          <tr className='snapshots-tabs-table__tr'>
            {tableColumns.map((column) => {
              return (
                <th
                  key={`filter-${column}`}
                  className='snapshots-tabs-table__td'
                >
                  <input
                    className='snapshots-tabs-table__input-text '
                    data-id={column}
                    type='text'
                    onChange={this.handleOnChangeFilter}
                    value={this.state.filters[column]}
                  />
                </th>
              );
            })}
          </tr>
        ) : null}
      </thead>
    );
  }

  renderBody(tableColumns, data) {
    return (
      <tbody>
        {data.length ? (
          data.map((dev) => {
            return (
              <tr key={`${dev.id}`} className='snapshots-tabs-table__tr'>
                {tableColumns.map((column) => (
                  <td
                    key={`${dev.id}-${column}`}
                    className='snapshots-tabs-table__td'
                  >
                    {this.getColumnContent(column, dev)}
                  </td>
                ))}
              </tr>
            );
          })
        ) : (
          <tr className='snapshots-tabs-table__tr'>
            <td colSpan='100' className='snapshots-tabs-table__td'>
              No data
            </td>
          </tr>
        )}
      </tbody>
    );
  }

  renderFooter(tableColumns, dataAfterAppliedFilters) {
    const { page, rowsPerPage } = this.state;
    const { snapshotsMsg } = this.props;
    const totalPages = Math.ceil(dataAfterAppliedFilters.length / rowsPerPage);
    const renderPagination = () => {
      const delta = 2;
      const left = page - delta;
      const right =
        page + delta + 1 + (left > 0 ? 0 : Math.min(delta - left, delta));
      const pages = Array.from({ length: totalPages }, (_v, k) => k + 1).filter(
        (i) => i && i >= left && i < right,
      );
      return pages.map((p) => {
        return (
          <span
            className={`snapshots-tabs-table__pagination-page ${p === page ? 'snapshots-tabs-table__pagination-page--active' : ''}`}
            key={p}
            onClick={this.handlePageClick(p)}
          >
            {p}
          </span>
        );
      });
    };

    return (
      <tfoot className='snapshots-tabs-table__pagination-foot'>
        <tr>
          <td colSpan={tableColumns.length + 1}>
            <i
              className={`snapshots-tabs-table__pagination-arrow ${page === 1 ? 'snapshots-tabs-table__pagination-arrow--disabled' : ''} fa fa-angle-double-left`}
              onClick={this.handlePageClick(1)}
            />
            <i
              className={`snapshots-tabs-table__pagination-arrow ${page === 1 ? 'snapshots-tabs-table__pagination-arrow--disabled' : ''} fa fa-angle-left`}
              onClick={page !== 1 ? this.handlePageClick(page - 1) : null}
            />
            {renderPagination()}
            <i
              className={`snapshots-tabs-table__pagination-arrow ${page === totalPages ? 'snapshots-tabs-table__pagination-arrow--disabled' : ''} fa fa-angle-right`}
              onClick={
                page !== totalPages ? this.handlePageClick(page + 1) : null
              }
            />
            <i
              className={`snapshots-tabs-table__pagination-arrow ${page === totalPages ? 'snapshots-tabs-table__pagination-arrow--disabled' : ''} fa fa-angle-double-right`}
              onClick={this.handlePageClick(totalPages)}
            />

            <select
              className='snapshots-tabs-table__pagination-select-pagination form-control'
              name='type'
              onChange={this.handleOnChangeRowsPerPage}
            >
              {pagination.map((p) => (
                <option key={`pagination-${p}`} value={p}>
                  {`${p} ${get(snapshotsMsg, 'discovery.rows', '')}`}
                </option>
              ))}
            </select>
            <span className='snapshots-tabs-table__count float-right'>
              <span className='mr-lgu-2'>
                {get(snapshotsMsg, 'discovery.total')}
              </span>
              <FormattedMessage
                id='tableItemsCount'
                defaultMessage='{itemsCount, plural, one {1 item} other {# items}}'
                values={{ itemsCount: dataAfterAppliedFilters.length }}
              />
            </span>
          </td>
        </tr>
      </tfoot>
    );
  }

  render() {
    const { columns, data, disableFooter, tableTitle } = this.props;
    const appliedFilters = this.applyFilters();
    const body = this.getData(appliedFilters);
    return (
      <Fragment>
        {data?.size ? (
          <div className='mb-lgu-6'>
            <div className='snapshots-tabs-table__top'>
              <div className='snapshots-tabs-table__title'>{tableTitle}</div>
            </div>
            <div>
              <Scrollbars autoHeight autoHeightMin='100%' autoHeightMax='100%'>
                <table className='table-condensed table-hover mb-0 table'>
                  {this.renderHeader(columns)}
                  {this.renderBody(columns, body)}
                  {!disableFooter && this.renderFooter(columns, appliedFilters)}
                </table>
              </Scrollbars>
            </div>
          </div>
        ) : null}
      </Fragment>
    );
  }
}
